import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import TopBar from "../components/common/TopBar";
import LivoraToast from "../components/common/LivoraToast";
import { uploadSupportImage } from "./supportUpload";
import {
  resolveSupportCategory,
  resolveSupportCategoryFromRouteType,
} from "./supportRoutes";
import "./SupportNewTicketPage.css";

const ISSUE_CATEGORIES = [
  { key: "payment", icon: "💳", defaultValue: "Payment" },
  { key: "withdrawal", icon: "💸", defaultValue: "Withdrawal" },
  { key: "calls", icon: "📞", defaultValue: "Calls" },
  { key: "messages", icon: "💬", defaultValue: "Messages" },
  { key: "verification", icon: "🪪", defaultValue: "Verification" },
  { key: "report", icon: "🚨", defaultValue: "Report / abuse" },
  { key: "technical", icon: "⚙️", defaultValue: "Technical" },
  { key: "other", icon: "✏️", defaultValue: "Other" },
];

export default function SupportNewTicketPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { type } = useParams(); // contact | issue | legacy category slug
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const preselectedCategory = useMemo(() => {
    const fromQuery = resolveSupportCategory(searchParams.get("category"));
    if (fromQuery) return fromQuery;
    return resolveSupportCategoryFromRouteType(type);
  }, [searchParams, type]);

  const safeType = useMemo(() => {
    if (type === "issue") return "issue";
    if (type === "contact") return "contact";
    if (preselectedCategory) return "issue";
    return "contact";
  }, [type, preselectedCategory]);

  const pageTitle = useMemo(() => {
    return safeType === "issue"
      ? t("report_problem", { defaultValue: "Report a problem" })
      : t("contact_us", { defaultValue: "Contact us" });
  }, [safeType, t]);

  const [category, setCategory] = useState(() => preselectedCategory || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    if (!preselectedCategory) return;
    setCategory((current) =>
      current === preselectedCategory ? current : preselectedCategory
    );
  }, [preselectedCategory]);

  const showToast = (messageText, type = "success") => {
    setToast({
      show: true,
      message: messageText,
      type,
    });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachment(file);
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetForm = () => {
    setCategory("");
    setSubject("");
    setMessage("");
    removeAttachment();
  };

  const submit = async () => {
    const user = auth.currentUser;

    if (!user) {
      showToast(
        t("auth.login_required", { defaultValue: "Please sign in first." }),
        "error"
      );
      return;
    }

    if (!category) {
      showToast(
        t("support.category_required", {
          defaultValue: "Choose the issue type first.",
        }),
        "info"
      );
      return;
    }

    if (subject.trim().length < 3) {
      showToast(
        t("support.subject_required", {
          defaultValue: "Write a clear subject for the request.",
        }),
        "info"
      );
      return;
    }

    if (message.trim().length < 10) {
      showToast(
        t("support.message_required", {
          defaultValue: "Write more details (at least 10 characters).",
        }),
        "info"
      );
      return;
    }

    setLoading(true);
    try {
      const createSupportTicket = httpsCallable(functions, "createSupportTicket");

      let uploaded = {
        attachmentName: null,
        attachmentSize: null,
        attachmentUrl: null,
        attachmentPath: null,
      };

      if (attachment) {
        uploaded = await uploadSupportImage({
          file: attachment,
          ticketId: "new",
          scope: "ticket",
        });
      }

      const res = await createSupportTicket({
        type: safeType,
        category,
        subject: subject.trim(),
        message: message.trim(),
        priority: "normal",
        attachmentName: uploaded.attachmentName,
        attachmentSize: uploaded.attachmentSize,
        attachmentUrl: uploaded.attachmentUrl,
        attachmentPath: uploaded.attachmentPath,
      });

      console.log("support ticket created:", res.data);

      resetForm();

      showToast(
        t("support.success", { defaultValue: "Your request was sent successfully." }),
        "success"
      );

      setTimeout(() => {
        navigate(-1);
      }, 700);
    } catch (e) {
      console.error("support ticket error:", e);
      showToast(
        t("support.error", { defaultValue: "Something went wrong while sending." }),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-new-root">
      <TopBar title={pageTitle} onBack={() => navigate(-1)} />

      <div className="support-new-page">
        <div className="support-new-card">
          <div className="support-hero">
            <div className="support-hero-icon">🎧</div>

            <h1 className="support-hero-title">
              {t("support.hero_title", {
                defaultValue: "How can we help?",
              })}
            </h1>

            <p className="support-hero-subtitle">
              {t("support.hero_subtitle", {
                defaultValue: "Send us your issue and we will review it as soon as possible.",
              })}
            </p>
          </div>

          <div className="support-section">
            <div className="support-section-title">
              {t("support.issue_type", { defaultValue: "Issue type" })}
            </div>

            <div className="support-chip-grid">
              {ISSUE_CATEGORIES.map((item) => {
                const active = category === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`support-chip ${active ? "active" : ""}`}
                    onClick={() => setCategory(item.key)}
                  >
                    <span className="support-chip-icon">{item.icon}</span>
                    <span className="support-chip-label">
                      {t(`support.categories.${item.key}`, {
                        defaultValue: item.defaultValue,
                      })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="support-form-block">
            <label className="support-label">
              {t("support.subject", { defaultValue: "Request subject" })}
            </label>
            <input
              className="support-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("support.subject_placeholder", {
                defaultValue: "Example: Problem with messages",
              })}
              maxLength={120}
            />
          </div>

          <div className="support-form-block">
            <label className="support-label">
              {t("support.message", { defaultValue: "Issue details" })}
            </label>
            <textarea
              className="support-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("support.message_placeholder", {
                defaultValue: "Write the details of your issue clearly here...",
              })}
              maxLength={1000}
            />
            <div className="support-counter">{message.length}/1000</div>
          </div>

          <div className="support-upload-card">
            <div className="support-upload-title">
              {t("support.attachments_optional", {
                defaultValue: "Attach image (optional)",
              })}
            </div>

            <div className="support-upload-subtitle">
              {t("support.attachments_hint", {
                defaultValue: "Add a screenshot to explain the issue more clearly.",
              })}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="support-hidden-file"
              onChange={onPickFile}
            />

            <button
              type="button"
              className="support-upload-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              {t("support.pick_image", { defaultValue: "Choose image" })}
            </button>

            {attachment && (
              <div className="support-file-preview">
                <div className="support-file-thumb-wrap">
                  <img
                    src={URL.createObjectURL(attachment)}
                    alt={t("support.attachment_preview_alt", {
                      defaultValue: "Attachment preview",
                    })}
                    className="support-file-thumb"
                  />
                </div>

                <div className="support-file-info">
                  <div className="support-file-name">{attachment.name}</div>
                  <div className="support-file-size">
                    {(attachment.size / 1024).toFixed(0)} KB
                  </div>
                </div>

                <button
                  type="button"
                  className="support-file-remove"
                  onClick={removeAttachment}
                  aria-label={t("support.remove_attachment", {
                    defaultValue: "Remove attachment",
                  })}
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className="support-note">
            {t("support.hint", {
              defaultValue:
                "Note: Please do not share passwords or secret codes in the message.",
            })}
          </div>

          <button
            className="support-submit"
            onClick={submit}
            disabled={loading}
          >
            {loading
              ? t("common.saving", { defaultValue: "Sending..." })
              : t("support.submit", { defaultValue: "Send" })}
          </button>
        </div>
      </div>

      <LivoraToast toast={toast} onClose={closeToast} />
    </div>
  );
}
